/**
 * Data-piece chips (UI-consistency spec 2026-07-11 §2): every reference
 * to a piece of data the workflow collects renders as the same small
 * tinted chip — in template text (via TemplateText), agenda dependsOn,
 * automation writes, checkpoint requires, and the Simulate rail.
 * The raw token stays available as the `title` tooltip.
 */
import type { ReactNode } from 'react'
import { TOKEN_RE, tokenLabel } from '@/utils/friendlyLabels'
import { splitSegments } from '@/utils/templateSegments'

export function TokenChip({ label, title }: { label: string; title?: string }) {
  return (
    <span
      title={title}
      className="inline-flex items-center rounded-md border border-primary/20 bg-primary/10 px-1.5 text-[11px] font-medium text-foreground align-baseline"
    >
      {label}
    </span>
  )
}

/** Token-chip pass over one run of text (no segment awareness). */
function TokenText({ text, slotLabels }: { text: string; slotLabels?: Record<string, string> }) {
  const nodes: ReactNode[] = []
  let last = 0
  for (const m of text.matchAll(TOKEN_RE)) {
    const i = m.index ?? 0
    if (i > last) nodes.push(text.slice(last, i))
    nodes.push(
      <TokenChip key={`${i}-${m[0]}`} label={tokenLabel(m[0], slotLabels)} title={m[0]} />,
    )
    last = i + m[0].length
  }
  if (last < text.length) nodes.push(text.slice(last))
  return <>{nodes}</>
}

// Module-private on purpose: TokenChip.tsx must export only components
// (react-refresh/only-export-components — see publishFindings.ts's note).
const OPTIONAL_SEGMENT_TITLE = 'Optional — shown only when every piece inside is known'

/**
 * `segments` (SA-308): render balanced `[[ … ]]` optional segments as an
 * "optional" chip wrapping the body — only where the engine honors them
 * (template rows, checkpoint text). Malformed brackets render literally; the
 * server's validate finding owns that complaint. Off by default because asks,
 * park/fallback and handoff messages keep `[[` literal at runtime.
 */
export function TemplateText({ text, slotLabels, segments = false }: {
  text: string
  slotLabels?: Record<string, string>
  segments?: boolean
}) {
  if (!segments) return <TokenText text={text} slotLabels={slotLabels} />
  const parts = splitSegments(text)
  if (!parts || !parts.some((p) => p.kind === 'segment')) {
    return <TokenText text={text} slotLabels={slotLabels} />
  }
  return (
    <>
      {parts.map((part, i) =>
        part.kind === 'segment' ? (
          <span
            key={i}
            title={OPTIONAL_SEGMENT_TITLE}
            className="inline rounded-md border border-dashed border-border px-1 align-baseline"
          >
            <span className="mr-1 text-[10px] uppercase text-muted-foreground">optional</span>
            <TokenText text={part.text} slotLabels={slotLabels} />
          </span>
        ) : (
          <TokenText key={i} text={part.text} slotLabels={slotLabels} />
        ),
      )}
    </>
  )
}
